// Archivo: TrasladosInventario.js (CORREGIDO PARA LOTES GRANDES > 30)

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, doc, query, orderBy, runTransaction, getDocs, where, serverTimestamp, writeBatch } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Modal from '../../ui/Modal';
import TransferForm from '../../ui/TransferForm';
import TransferBatchDetailView from '../../ui/TransferBatchDetailView';

const PrintIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2-4h6m-6-4v2m6-4v2" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 hover:text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;

// --- INICIO DE NUEVA FUNCIÓN AUXILIAR ---
const getDocsInChunks = async (collectionRef, field, values) => {
    if (!values.length) return [];
    const chunks = [];
    for (let i = 0; i < values.length; i += 30) {
        chunks.push(values.slice(i, i + 30));
    }
    const results = [];
    for (const chunk of chunks) {
        const q = query(collectionRef, where(field, 'in', chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach(doc => results.push(doc));
    }
    return results;
};
// --- FIN DE NUEVA FUNCIÓN AUXILIAR ---


export default function TrasladosInventario({ currentUser, db, showModal, catalogs, stock, permissions }) {
    const [transfers, setTransfers] = useState([]);
    const [batchItems, setBatchItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedBatchForDetail, setSelectedBatchForDetail] = useState(null);
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        const q = query(collection(db, 'inventory_transfers'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setTransfers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, () => {
            setIsLoading(false);
            showModal("Error", "No se pudo cargar el historial de traslados.");
        });
        return () => unsubscribe();
    }, [db, showModal]);

    const groupedTransfers = useMemo(() => {
        if (!transfers.length || !catalogs) return [];
        const groups = transfers.reduce((acc, t) => {
            const batchId = t.batchId || t.id;
            if (!acc[batchId]) {
                acc[batchId] = {
                    batchId, status: t.status, requester: t.requester,
                    requesterId: t.requesterId, approver: t.approver,
                    createdAt: t.createdAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    approvedAt: t.approvedAt?.toDate().toLocaleString('es-GT', { timeZone: 'America/Guatemala' }),
                    items: [], totalQuantity: 0,
                    origins: new Set(), destinations: new Set(),
                };
            }
            const product = catalogs.products.find(p => p.id === t.productId);
            const design = catalogs.designs.find(d => d.id === t.designId);
            const originLocation = catalogs.locations.find(l => l.id === t.originLocationId);
            const destinationLocation = catalogs.locations.find(l => l.id === t.destinationLocationId);
            
            const designName = design ? `${design.name}${t.designSize ? ` (${t.designSize})` : ''}` : 'Liso';

            acc[batchId].items.push({
                ...t, 
                productName: product?.name, 
                designName: designName,
                originLocationName: originLocation?.name, 
                destinationLocationName: destinationLocation?.name,
            });
            acc[batchId].totalQuantity += t.quantity;
            if (originLocation) acc[batchId].origins.add(originLocation.name);
            if (destinationLocation) acc[batchId].destinations.add(destinationLocation.name);
            return acc;
        }, {});
        return Object.values(groups).map(batch => ({
            ...batch,
            originText: batch.origins.size > 1 ? 'Varios' : Array.from(batch.origins)[0] || 'N/A',
            destinationText: batch.destinations.size > 1 ? 'Varios' : Array.from(batch.destinations)[0] || 'N/A',
        }));
    }, [transfers, catalogs]);
    
    const batchTotalQuantity = useMemo(() => {
        return batchItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
    }, [batchItems]);

    const availableStockForForm = useMemo(() => {
        if (!stock) return [];
        const subtractions = batchItems.reduce((acc, item) => {
            if (editingItem && item.tempId === editingItem.tempId) {
                return acc;
            }
            acc[item.stockItem.id] = (acc[item.stockItem.id] || 0) + item.quantity;
            return acc;
        }, {});
        return stock
            .map(item => {
                if (subtractions[item.id]) {
                    return { ...item, quantity: item.quantity - subtractions[item.id] };
                }
                return item;
            })
            .filter(item => item.quantity > 0);
    }, [stock, batchItems, editingItem]);

    const canUserApproveBatch = (batch) => {
        if (batch.status !== 'pendiente' || !permissions?.INVENTORY?.approve_transfer) return false;
        if (currentUser.role === 'administrador' || currentUser.role === 'supervisor') return true;
        if (currentUser.uid === batch.requesterId) return false;
        return batch.items.every(item => item.destinationLocationId === currentUser.location);
    };

    const handleViewDetails = (batch) => {
        setSelectedBatchForDetail(batch);
        setIsDetailModalOpen(true);
    };

    const handleAddToBatch = (formData) => {
        const destinationLocation = catalogs.locations.find(l => l.id === formData.destinationLocationId);
        const design = catalogs.designs.find(d => d.id === formData.stockItem.designId);
        const designName = design ? `${design.name}${formData.stockItem.designSize ? ` (${formData.stockItem.designSize})` : ''}` : 'Liso';
        setBatchItems(prev => [...prev, { 
            ...formData, 
            tempId: Date.now(), 
            destinationLocationName: destinationLocation?.name,
            designName: designName 
        }]);
        setIsModalOpen(false);
    };
    
    const handleUpdateBatchItem = (formData) => {
        const destinationLocation = catalogs.locations.find(l => l.id === formData.destinationLocationId);
        const design = catalogs.designs.find(d => d.id === formData.stockItem.designId);
        const designName = design ? `${design.name}${formData.stockItem.designSize ? ` (${formData.stockItem.designSize})` : ''}` : 'Liso';
        const updatedItem = {
            ...formData,
            tempId: editingItem.tempId,
            destinationLocationName: destinationLocation?.name,
            designName: designName
        };
        setBatchItems(prevItems => prevItems.map(item => item.tempId === editingItem.tempId ? updatedItem : item));
        setEditingItem(null);
        setIsModalOpen(false);
    };

    const handleStartEdit = (itemToEdit) => {
        setEditingItem(itemToEdit);
        setIsModalOpen(true);
    };

    const handleRemoveFromBatch = (tempId) => {
        setBatchItems(prev => prev.filter(item => item.tempId !== tempId));
    };

    const handleSaveTransferBatch = async () => {
        if (batchItems.length === 0) return showModal("Lote Vacío", "No hay traslados en el lote.");
        setIsLoading(true);
        const batchId = `tras-${Date.now()}`;
        const batch = writeBatch(db);
        batchItems.forEach(item => {
            const transferRef = doc(collection(db, 'inventory_transfers'));
            batch.set(transferRef, {
                batchId, 
                productId: item.stockItem.productId, 
                designId: item.stockItem.designId || 'NONE',
                designSize: item.stockItem.designSize || null,
                size: item.stockItem.size, 
                color: item.stockItem.color, 
                quantity: item.quantity,
                originLocationId: item.stockItem.locationId, 
                destinationLocationId: item.destinationLocationId,
                status: 'pendiente', 
                requester: currentUser.name, 
                requesterId: currentUser.uid,
                createdAt: serverTimestamp()
            });
        });
        try {
            await batch.commit();
            showModal("Éxito", "Lote de traslados solicitado. Esperando aprobación.");
            setBatchItems([]);
        } catch (error) {
            showModal("Error", `No se pudo crear el lote de traslados: ${error.message}`);
        }
        setIsLoading(false);
    };

    const handleRejectTransferBatch = async (batchToReject) => {
        setIsLoading(true);
        const batch = writeBatch(db);
        try {
            for (const item of batchToReject.items) {
                const transferRef = doc(db, 'inventory_transfers', item.id);
                batch.update(transferRef, { 
                    status: 'rechazado', 
                    approver: currentUser.name, 
                    approvedAt: serverTimestamp() 
                });
            }
            await batch.commit();
            showModal("Éxito", "El lote de traslados ha sido rechazado.");
        } catch(error) {
            showModal("Error", `No se pudo rechazar el lote: ${error.message}`);
        }
        setIsLoading(false);
    };
    
    const handleApproveTransferBatch = async (batchToApprove) => {
        setIsLoading(true);
        try {
            const aggregatedItems = batchToApprove.items.reduce((acc, item) => {
                const finalDesignId = item.designId || 'NONE';
                const finalDesignSize = (item.designSize || '').trim() || 'NONE';
                const originSku = `${item.productId}_${finalDesignId}_${item.size}_${item.color}_${finalDesignSize}_${item.originLocationId}`;
                const destSku = `${item.productId}_${finalDesignId}_${item.size}_${item.color}_${finalDesignSize}_${item.destinationLocationId}`;
                const key = `${originSku}#${destSku}`;

                if (!acc[key]) {
                    acc[key] = { ...item, quantity: 0, originSku, destSku };
                }
                acc[key].quantity += item.quantity;
                return acc;
            }, {});
            const finalBatch = Object.values(aggregatedItems);

            const allSkus = new Set();
            finalBatch.forEach(item => {
                allSkus.add(item.originSku);
                allSkus.add(item.destSku);
            });
            
            if (allSkus.size === 0) {
                throw new Error("No hay productos válidos para trasladar.");
            }

            // --- INICIO DE CORRECCIÓN ---
            // Se utiliza la nueva función para manejar lotes grandes.
            const stockDocs = await getDocsInChunks(collection(db, 'inventory_stock'), 'sku', Array.from(allSkus));
            const stockDocsMap = new Map(stockDocs.map(d => [d.data().sku, d.ref]));
            // --- FIN DE CORRECCIÓN ---

            await runTransaction(db, async (transaction) => {
                const readDocsData = new Map();
                const getPromises = [];

                for (const ref of stockDocsMap.values()) {
                    getPromises.push(transaction.get(ref));
                }
                const snapshots = await Promise.all(getPromises);
                snapshots.forEach(snap => {
                    readDocsData.set(snap.ref.path, snap.data());
                });

                for (const item of finalBatch) {
                    const productDescription = `${item.productName} (${item.size}, ${item.color}) ${item.designName !== 'Liso' ? `- ${item.designName}` : '(Liso)'}`;
                    
                    const originRef = stockDocsMap.get(item.originSku);
                    if (!originRef) { throw new Error(`Stock no encontrado en origen para: ${productDescription}.`); }

                    const originData = readDocsData.get(originRef.path);
                    if (!originData || originData.quantity < item.quantity) { throw new Error(`Stock insuficiente para ${productDescription}. Disponibles: ${originData?.quantity || 0}, Solicitados: ${item.quantity}.`);}
                    
                    transaction.update(originRef, { quantity: originData.quantity - item.quantity });

                    const destRef = stockDocsMap.get(item.destSku);
                    if (destRef) {
                        const destData = readDocsData.get(destRef.path);
                        transaction.update(destRef, { quantity: destData.quantity + item.quantity });
                    } else {
                        const newStockRef = doc(collection(db, 'inventory_stock'));
                        transaction.set(newStockRef, {
                            productId: item.productId, 
                            designId: item.designId || 'NONE', 
                            designSize: item.designSize || null,
                            size: item.size, 
                            color: item.color, 
                            quantity: item.quantity, 
                            locationId: item.destinationLocationId, 
                            sku: item.destSku,
                        });
                    }
                }
                
                for (const originalItem of batchToApprove.items) {
                    const transferRef = doc(db, 'inventory_transfers', originalItem.id);
                    transaction.update(transferRef, { status: 'aprobado', approver: currentUser.name, approvedAt: serverTimestamp() });
                }
            });
            showModal("Éxito", "Lote de traslados aprobado y stock actualizado.");
        } catch (error) {
            console.error("Error detallado en la transacción de traslado:", error);
            showModal("Error en Transacción", error.message);
        }
        setIsLoading(false);
    };

    const handlePrintBatch = (batch) => {
        const doc = new jsPDF();
        const logoImg = document.getElementById('company-logo-for-pdf');
        if (logoImg) doc.addImage(logoImg, 'PNG', 15, 12, 50, 15);
        doc.setFontSize(18);
        doc.text(`Comprobante de Traslado de Lote`, 15, 40);
        doc.setFontSize(12);
        doc.text(`ID del Lote: ${batch.batchId}`, 15, 47);
        doc.setFontSize(10);
        doc.setTextColor(100);
        const statusText = batch.status.charAt(0).toUpperCase() + batch.status.slice(1);
        const requesterLine = `Solicitado por: ${batch.requester || 'N/A'} el ${batch.createdAt || 'N/A'}`;
        const approverLine = batch.status !== 'pendiente' 
            ? `${batch.status === 'aprobado' ? 'Aprobado' : 'Rechazado'} por: ${batch.approver || 'N/A'} el ${batch.approvedAt || 'N/A'}`
            : null;
        const generatorName = currentUser?.name || 'Usuario Desconocido';
        const generationDate = new Date().toLocaleString('es-GT', { timeZone: 'America/Guatemala' });
        const generatorLine = `Generado por: ${generatorName} el ${generationDate}`;
        let tableStartY = 62;
        doc.text(requesterLine, 15, 56);
        doc.setFont('helvetica', 'bold');
        doc.text(`Estado: ${statusText}`, 140, 56);
        doc.setFont('helvetica', 'normal');
        if (approverLine) {
            doc.text(approverLine, 15, 62);
            doc.text(generatorLine, 15, 68);
            tableStartY = 76;
        } else {
            doc.text(generatorLine, 15, 62);
            tableStartY = 70;
        }
        const tableColumns = ["Producto", "Diseño", "Talla", "Color", "Origen", "Destino", "Cant."];
        const tableRows = batch.items.map(item => [
            item.productName, item.designName || 'Liso', item.size, item.color,
            item.originLocationName, item.destinationLocationName, item.quantity
        ]);
        autoTable(doc, { 
            head: [tableColumns], 
            body: tableRows, 
            startY: tableStartY,
            headStyles: { fillColor: [22, 160, 133] } 
        });
        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Cantidad Total de Productos en el Lote: ${batch.totalQuantity}`, 15, finalY + 10);
        doc.save(`traslado-lote-${batch.batchId}.pdf`);
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'aprobado': return 'bg-green-100 text-green-700';
            case 'pendiente': return 'bg-yellow-100 text-yellow-700';
            case 'rechazado': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };
    
    const handleCancelModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md space-y-8">
            <img id="company-logo-for-pdf" src="/aralellum.png" style={{ display: 'none' }} alt="Logo" />
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">Crear Lote de Traslado</h3>
                    {permissions?.INVENTORY?.create_transfer && (
                        <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} className="btn-primary">+ Añadir Traslado al Lote</button>
                    )}
                </div>
                <p className="text-gray-600 mb-6">Añade uno o más traslados a un lote y luego solicítalo para su aprobación.</p>
            </div>

            {batchItems.length > 0 && (
                <div className="p-4 border rounded-lg bg-sky-50">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold">Lote Pendiente ({batchItems.length} traslados)</h4>
                        <button onClick={handleSaveTransferBatch} className="btn-success">Solicitar Lote de Traslado</button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-200">
                                <tr>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Producto</th>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Origen</th>
                                    <th className="p-2 text-left text-sm font-semibold text-gray-600">Destino</th>
                                    <th className="p-2 text-right text-sm font-semibold text-gray-600">Cantidad</th>
                                    <th className="p-2 text-center text-sm font-semibold text-gray-600">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batchItems.map(item => (
                                    <tr key={item.tempId} className="border-b">
                                        <td className="p-2">
                                            <p className="font-medium">{item.stockItem.productName} ({item.stockItem.size}, {item.stockItem.color})</p>
                                            {item.designName !== 'Liso' && <p className="text-xs text-indigo-500">{item.designName}</p>}
                                        </td>
                                        <td className="p-2">{item.stockItem.locationName}</td>
                                        <td className="p-2">{item.destinationLocationName}</td>
                                        <td className="p-2 text-right font-bold">{item.quantity}</td>
                                        <td className="p-2 text-center">
                                            <div className="flex justify-center items-center space-x-2">
                                                <button onClick={() => handleStartEdit(item)} className="text-sky-600 hover:text-sky-800 p-1" title="Editar traslado">
                                                    <EditIcon />
                                                </button>
                                                <button onClick={() => handleRemoveFromBatch(item.tempId)} className="p-1" title="Quitar del lote">
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-gray-300">
                                    <td colSpan="3" className="p-2 text-right font-semibold text-gray-800">Total del Lote:</td>
                                    <td className="p-2 text-right font-bold text-lg text-gray-800">{batchTotalQuantity}</td>
                                    <td className="p-2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Historial de Lotes de Traslado</h3>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1200px]">
                         <thead className="bg-gray-100">
                            <tr>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Fecha Sol.</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">ID Lote</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Origen</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Destino</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Solicitante</th>
                                <th className="p-3 text-right text-sm font-semibold text-gray-600">Cant. Total</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-600">Estado</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-600">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan="8" className="text-center p-4">Cargando...</td></tr>) : groupedTransfers.map(batch => (
                                <tr key={batch.batchId} className="border-b">
                                    <td className="p-3 text-sm">{batch.createdAt}</td>
                                    <td className="p-3 font-mono text-xs">{batch.batchId}</td>
                                    <td className="p-3">{batch.originText}</td>
                                    <td className="p-3">{batch.destinationText}</td>
                                    <td className="p-3">{batch.requester}</td>
                                    <td className="p-3 text-right font-bold">{batch.totalQuantity}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(batch.status)}`}>{batch.status}</span>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex gap-4 justify-center items-center">
                                            {canUserApproveBatch(batch) && (
                                                <>
                                                    <button onClick={() => handleApproveTransferBatch(batch)} className="btn-success text-xs">Aprobar</button>
                                                    <button onClick={() => handleRejectTransferBatch(batch)} className="btn-secondary text-xs bg-red-200">Rechazar</button>
                                                </>
                                            )}
                                            <button onClick={() => handleViewDetails(batch)} title="Ver Detalles del Lote">
                                                <EyeIcon />
                                            </button>
                                            <button onClick={() => handlePrintBatch(batch)} title="Imprimir Comprobante">
                                                <PrintIcon />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                     {groupedTransfers.length === 0 && !isLoading && <p className="text-center text-gray-500 py-8">No hay lotes de traslado registrados.</p>}
                </div>
            </div>
            
            <Modal isOpen={isModalOpen} onClose={handleCancelModal} title={editingItem ? "Editar Traslado del Lote" : "Añadir Traslado al Lote"}>
                <TransferForm 
                    onSave={editingItem ? handleUpdateBatchItem : handleAddToBatch} 
                    onCancel={handleCancelModal} 
                    stock={availableStockForForm}
                    initialData={editingItem}
                    {...{ locations: catalogs?.locations, currentUser, catalogs, showModal }} 
                />
            </Modal>

            <Modal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} title="Detalle de Lote de Traslado">
                <TransferBatchDetailView batch={selectedBatchForDetail} />
            </Modal>
        </div>
    );
};